/**
 * Reusable Case Matter Select Component
 * Uses react-select-async-paginate for efficient loading and pagination
 * Supports search and infinite scroll
 * Styled to match PrimeReact Dropdown components
 */

import React, { useMemo } from 'react';
import { AsyncPaginate } from 'react-select-async-paginate';
import { Tooltip } from 'primereact/tooltip';
import useIsMobileView from 'hooks/useIsMobileView';
import { loadCaseMatterOptions } from 'hooks/queries/useCaseMattersPaginated'; // v2: list-case-matters-v2
import '../shared/AsyncSelect.scss';
import '../PracticeAreaSelect/PracticeAreaSelectV2.scss';
import './CaseMatterSelect.scss';

/** Chip / menu label — prefer API `matter_name`, then react-select `label`. */
function getCaseMatterOptionLabel(opt) {
  if (opt == null || opt === '') return '';
  if (typeof opt !== 'object') return String(opt);
  return opt.matter_name ?? opt.label ?? String(opt.value ?? opt.case_matter_id ?? '');
}

/** Stable id for option equality (v2 rows use `case_matter_id` or `value`). */
function getCaseMatterOptionValue(opt) {
  if (opt == null || opt === '') return '';
  if (typeof opt !== 'object') return String(opt);
  const v = opt.case_matter_id ?? opt.value ?? opt.sk;
  return v != null ? String(v) : '';
}

function normalizeCaseMatterValue(isMulti, value) {
  if (!isMulti) {
    return value ?? null;
  }
  if (Array.isArray(value)) {
    return value;
  }
  if (value == null || value === '') {
    return [];
  }
  return [value];
}

function OptionLabel({ tooltipText, children, data }) {
  const safeTooltipClass = `case-matter-option-tooltip-${String(getCaseMatterOptionValue(data) || tooltipText || 'option')
    .replace(/[^a-zA-Z0-9_-]/g, '_')
    .slice(0, 80)}`;
  return (
    <>
      <span
        className={`pa-select-v2-option__label ${safeTooltipClass}`}
        style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
      >
        {children}
      </span>
      <Tooltip target={`.${safeTooltipClass}`} content={tooltipText} position="top" />
    </>
  );
}

/** Single-select menu row */
function OptionWithTooltip({ innerRef, innerProps, isFocused, isSelected, isDisabled, children, data }) {
  const tooltipText = getCaseMatterOptionLabel(data) || (typeof children === 'string' ? children : '');
  return (
    <div
      ref={innerRef}
      {...innerProps}
      className={[
        'async-select-custom__option',
        isFocused ? 'async-select-custom__option--is-focused' : '',
        isSelected ? 'async-select-custom__option--is-selected' : '',
        isDisabled ? 'async-select-custom__option--is-disabled' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <OptionLabel tooltipText={tooltipText} data={data}>
        {children}
      </OptionLabel>
    </div>
  );
}

/** Multi-select menu row — same checkbox DOM/classes as PracticeAreaSelectV2. */
function CheckboxOption({ innerRef, innerProps, isFocused, isSelected, isDisabled, children, data }) {
  const tooltipText = getCaseMatterOptionLabel(data) || (typeof children === 'string' ? children : '');
  return (
    <div
      ref={innerRef}
      {...innerProps}
      className={[
        'async-select-custom__option',
        isFocused ? 'async-select-custom__option--is-focused' : '',
        isSelected ? 'async-select-custom__option--is-selected' : '',
        isDisabled ? 'async-select-custom__option--is-disabled' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className={`pa-select-v2-option${isDisabled ? ' pa-select-v2-option--disabled' : ''}`}>
        <span className={`pa-select-v2-option__checkbox${isSelected ? ' pa-select-v2-option__checkbox--checked' : ''}`}>
          {isSelected && (
            <svg className="pa-select-v2-option__checkbox__tick" viewBox="0 0 10 10">
              <polyline points="1.5,5 4,7.5 8.5,2.5" />
            </svg>
          )}
        </span>
        <OptionLabel tooltipText={tooltipText} data={data}>
          {children}
        </OptionLabel>
      </div>
    </div>
  );
}

/**
 * CaseMatterSelect Component
 *
 * @param {Object} props - Component props
 * @param {string|Object|null|Array<Object>} props.value - Single: id string or `{ value, label }` or null. Multi: array of option objects (empty array when none).
 * @param {Function} props.onChange - Change handler `(value, actionMeta) => void` — multi mode passes an array of options or null when cleared.
 * @param {boolean} [props.isMulti=false] - Allow multiple selections (react-select multi).
 * @param {string} props.placeholder - Placeholder text
 * @param {boolean} props.isClearable - Show clear button
 * @param {boolean} props.isDisabled - Disable the select
 * @param {boolean} props.isSearchable - Enable search (default: true)
 * @param {string} props.className - Additional CSS classes
 * @param {string|Array} props.practiceAreaId - Filter by practice area ID(s)
 * @param {boolean} [props.isPiCase=false] — pass true on PI-only screens (matches v2 `is_pi_case`)
 * @param {number} props.pageSize - Items per page (default: 20)
 * @param {Object} props.styles - Custom react-select styles
 * @param {string} props.name - Input name attribute
 * @param {string} props.id - Input ID attribute
 * @param {Function} props.onBlur - Blur handler
 * @param {Function} props.onFocus - Focus handler
 * @param {Element|null|undefined} [props.menuPortalTarget] - When set, portalling behavior (undefined = auto: body on mobile only)
 * @param {string|undefined} [props.menuPosition] - react-select menu position (undefined = auto: fixed on mobile)
 * @returns {JSX.Element}
 */
function CaseMatterSelect({
  value = null,
  onChange,
  isMulti = false,
  placeholder = 'Select case matter',
  isClearable = true,
  isDisabled = false,
  isSearchable = true,
  className = '',
  practiceAreaId = '',
  isPiCase = false,
  pageSize = 20,
  styles = {},
  name,
  id,
  onBlur,
  onFocus,
  menuPortalTarget,
  menuPosition,
  components: componentsFromProps,
  hideSelectedOptions: hideSelectedFromProps,
  cacheUniqs,
  ...rest
}) {
  const isMobile = useIsMobileView();

  const resolvedMenuPortalTarget = useMemo(() => {
    if (menuPortalTarget !== undefined) return menuPortalTarget;
    if (isMobile && typeof document !== 'undefined') return document.body;
    return null;
  }, [menuPortalTarget, isMobile]);

  const resolvedMenuPosition = useMemo(() => {
    if (menuPosition !== undefined) return menuPosition;
    return isMobile ? 'fixed' : 'absolute';
  }, [menuPosition, isMobile]);

  const resolvedValue = useMemo(() => normalizeCaseMatterValue(isMulti, value), [isMulti, value]);

  const components = useMemo(() => {
    const Option = isMulti ? CheckboxOption : OptionWithTooltip;
    return { ...componentsFromProps, Option };
  }, [isMulti, componentsFromProps]);
  // Memoize the load options function with config
  const loadOptions = useMemo(
    () => (search, loadedOptions, additional) =>
      loadCaseMatterOptions(search, loadedOptions, additional, {
        pageSize,
        practiceAreaId,
        is_pi_case: isPiCase,
      }),
    [pageSize, practiceAreaId, isPiCase]
  );

  // Debounce time for search input (in milliseconds)
  const debounceTimeout = 300;

  /**
   * Initial cursor for v2 list. `hasMore` from `loadCaseMatterOptions` follows API `has_next`;
   * `has_previous` on `additional` is informational (AsyncPaginate only loads more on bottom scroll).
   */
  const additional = {
    page: 1,
    hasNext: true,
    hasPrevious: false,
  };

  // Merge custom styles with menu styling to prevent ResizeObserver issues
  const mergedStyles = useMemo(
    () => ({
      control: (base) => ({
        ...base,
        ...(isMobile && { maxWidth: '100%' }),
      }),
      menu: (base) => ({
        ...base,
        zIndex: 9999,
        ...(isMobile && {
          maxWidth: 'min(100vw - 24px, 560px)',
          width: 'min(100vw - 24px, 560px)',
        }),
      }),
      menuPortal: (base) => ({
        ...base,
        zIndex: 9999,
      }),
      ...styles,
    }),
    [isMobile, styles]
  );

  const resolvedCacheUniqs = useMemo(() => {
    const fromProp = Array.isArray(cacheUniqs) ? cacheUniqs : cacheUniqs != null ? [cacheUniqs] : [];
    return [...fromProp, practiceAreaId, isPiCase ? 1 : 0];
  }, [cacheUniqs, practiceAreaId, isPiCase]);

  return (
    <div
      className={isMobile ? 'case-matter-select-root case-matter-select-root--mobile' : 'case-matter-select-root'}
      style={isMobile ? { minWidth: 0, maxWidth: '100%' } : { maxWidth: '100%' }}
    >
      <AsyncPaginate
        value={resolvedValue}
        defaultOptions={false}
        loadOptions={loadOptions}
        onChange={onChange}
        isMulti={isMulti}
        closeMenuOnSelect={!isMulti}
        placeholder={placeholder}
        isClearable={isClearable}
        isDisabled={isDisabled}
        isSearchable={isSearchable}
        className={className}
        classNamePrefix="async-select-custom"
        styles={mergedStyles}
        additional={additional}
        debounceTimeout={debounceTimeout}
        cacheUniqs={resolvedCacheUniqs}
        name={name}
        id={id}
        onBlur={onBlur}
        onFocus={onFocus}
        getOptionLabel={getCaseMatterOptionLabel}
        getOptionValue={getCaseMatterOptionValue}
        loadingMessage={() => 'Loading case matters...'}
        noOptionsMessage={({ inputValue }) => (inputValue ? `No case matters found for "${inputValue}"` : 'No case matters available')}
        menuShouldScrollIntoView={false}
        closeMenuOnScroll={(e) => e.target === document}
        {...rest}
        menuPortalTarget={resolvedMenuPortalTarget}
        menuPosition={resolvedMenuPosition}
        components={components}
        hideSelectedOptions={isMulti ? false : hideSelectedFromProps}
      />
    </div>
  );
}

export default CaseMatterSelect;
